/* Campaign data is rendered by sections/promotion-engine.liquid. */
class PromotionEngine {
  constructor(config, status) {
    this.campaigns = (config?.campaigns || []).filter((campaign) => campaign.status === 'active');
    this.status = status;
    this.syncing = false;
    this.syncRequested = false;
    this.syncPromise = null;
  }

  async request(url, options) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  message(text, state) {
    if (!this.status) return;
    // Successful cart reconciliation is intentionally silent. Only errors
    // should become a visible storefront message.
    const visible = Boolean(text) && state === 'error';
    this.status.hidden = !visible;
    this.status.dataset.state = state || '';
    this.status.textContent = text || '';
  }

  ids(values) { return new Set((values || []).map((value) => String(value).trim()).filter(Boolean)); }

  eligible(cart, campaign) {
    const products = this.ids(campaign.eligibility?.product_ids);
    const variants = this.ids(campaign.eligibility?.variant_ids);
    return cart.items.filter((item) => {
      if (item.properties?._wsp_promotion_gift === 'true') return false;
      return variants.size ? variants.has(String(item.variant_id)) : products.has(String(item.product_id));
    });
  }

  evaluate(cart, campaign) {
    const lines = this.eligible(cart, campaign);
    const quantity = lines.reduce((sum, line) => sum + line.quantity, 0);
    const amount = lines.reduce((sum, line) => sum + line.final_line_price, 0);
    const same = campaign.eligibility?.mix_rule !== 'same_product' || new Set(lines.map((line) => line.product_id)).size <= 1;
    const conditions = campaign.conditions || {};
    return {
      quantity, amount,
      qualified: same && quantity >= Number(conditions.minimum_quantity || 1) && amount >= Number(conditions.minimum_amount || 0),
      overLimit: Number(conditions.maximum_quantity || 0) > 0 && quantity > Number(conditions.maximum_quantity),
    };
  }

  gifts(campaign, result) {
    if (!result.qualified || result.overLimit) return new Map();
    const tiers = (campaign.gift_tiers || []).filter((tier) => result.quantity >= Number(tier.minimum_quantity || 0) && result.amount >= Number(tier.minimum_amount || 0))
      .sort((a, b) => Number(b.minimum_amount || 0) - Number(a.minimum_amount || 0) || Number(b.minimum_quantity || 0) - Number(a.minimum_quantity || 0));
    const selected = campaign.gift_mode === 'cumulative' ? tiers : tiers.slice(0, 1);
    return selected.reduce((resultMap, tier) => {
      (tier.variant_ids || []).forEach((id, index) => {
        const quantity = Number((tier.quantities || [])[index] || 0);
        id = String(id).trim();
        if (id && quantity) resultMap.set(id, (resultMap.get(id) || 0) + quantity);
      });
      return resultMap;
    }, new Map());
  }

  async change(key, quantity) {
    return this.request('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
  }

  async update(updates) {
    return this.request('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
  }

  async add(campaign, variantId, quantity) {
    return this.request('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(variantId), quantity, properties: { _wsp_campaign: campaign.id, _wsp_promotion_gift: 'true' } }] }) });
  }

  async reconcile(cart, campaign, result) {
    const desired = this.gifts(campaign, result);
    const configuredGiftVariants = this.ids((campaign.gift_tiers || []).flatMap((tier) => tier.variant_ids || []));
    const existing = cart.items.filter((item) => {
      const isConfiguredGift = configuredGiftVariants.has(String(item.variant_id));
      const belongsToCampaign = item.properties?._wsp_campaign === campaign.id;
      const isGiftLine = item.properties?._wsp_promotion_gift === 'true' || Number(item.final_price) === 0;
      return isConfiguredGift && (belongsToCampaign || isGiftLine);
    });
    const byVariant = new Map();
    const updates = {};
    existing.forEach((line) => byVariant.set(String(line.variant_id), [...(byVariant.get(String(line.variant_id)) || []), line]));
    for (const [variantId, lines] of byVariant) {
      const target = desired.get(variantId) || 0;
      let remaining = target;
      for (const line of lines) {
        const quantity = Math.min(line.quantity, remaining);
        remaining -= quantity;
        if (quantity !== line.quantity) updates[line.key] = quantity;
      }
      const current = lines.reduce((sum, line) => sum + line.quantity, 0);
      if (current < target) desired.set(variantId, target - current);
      else desired.delete(variantId);
    }
    if (Object.keys(updates).length) await this.update(updates);
    for (const [variantId, quantity] of desired) await this.add(campaign, variantId, quantity);
  }

  async enforceMaximum(cart, campaign, result) {
    const maximum = Number(campaign.conditions?.maximum_quantity || 0);
    if (!maximum || !result.overLimit) return cart;
    let overflow = result.quantity - maximum;
    const previous = new Map((this.lastCart?.items || []).map((line) => [line.key, line.quantity]));
    const lines = this.eligible(cart, campaign);
    const increased = lines
      .map((line) => ({ line, delta: line.quantity - Number(previous.get(line.key) || 0) }))
      .filter((entry) => entry.delta > 0)
      .sort((a, b) => b.delta - a.delta);

    for (const entry of increased) {
      if (overflow <= 0) break;
      const remove = Math.min(entry.delta, overflow);
      await this.change(entry.line.key, entry.line.quantity - remove);
      overflow -= remove;
    }

    if (overflow > 0) {
      for (const line of [...lines].reverse()) {
        if (overflow <= 0) break;
        const remove = Math.min(line.quantity, overflow);
        await this.change(line.key, line.quantity - remove);
        overflow -= remove;
      }
    }

    return this.request('/cart.js');
  }

  applyMaximumControls(cart, campaign) {
    const maximum = Number(campaign.conditions?.maximum_quantity || 0);
    if (!maximum) return;
    const quantity = this.evaluate(cart, campaign).quantity;
    const remaining = Math.max(0, maximum - quantity);
    const variants = this.ids(campaign.eligibility?.variant_ids);
    document.querySelectorAll('cart-quantity-selector-component[data-variant-id]').forEach((selector) => {
      if (!variants.has(String(selector.dataset.variantId))) return;
      const input = selector.querySelector('input');
      if (!input) return;
      input.max = String(Number(input.value || 0) + remaining);
      selector.updateButtonStates?.();
    });
  }

  async sync() {
    if (!this.campaigns.length) return;
    this.syncRequested = true;
    if (this.syncing) return this.syncPromise;
    this.syncing = true;
    this.syncPromise = (async () => {
      while (this.syncRequested) {
        this.syncRequested = false;
        this.message('Updating promotion…', 'loading');
        let cart = await this.request('/cart.js');
        let note = '';
        for (const campaign of this.campaigns) {
          let result = this.evaluate(cart, campaign);
          const wasOverLimit = result.overLimit;
          if (result.overLimit) {
            note = campaign.limit_message || 'Promotion purchase limit reached.';
            cart = await this.enforceMaximum(cart, campaign, result);
            result = this.evaluate(cart, campaign);
          }
          await this.reconcile(cart, campaign, result);
          cart = await this.request('/cart.js');
          this.applyMaximumControls(cart, campaign);
          if (result.qualified && !wasOverLimit) note = campaign.qualified_message || '';
        }
        this.lastCart = cart;
        this.renderPwp();
        this.message(note, note ? 'success' : '');
        document.dispatchEvent(new CustomEvent('wsp:promotion-synced', { detail: { cart } }));
      }
    })();
    try {
      await this.syncPromise;
    } catch (error) {
      console.error('[WSP Promotion Engine]', error);
      this.message('Promotion could not be updated. Your cart was not cleared; please refresh and try again.', 'error');
    } finally {
      this.syncing = false;
      this.syncPromise = null;
    }
  }

  renderPwp() {
    const panel = document.getElementById('wsp-promotion-pwp');
    if (!panel) return;
    const items = this.campaigns.flatMap((campaign) => (campaign.pwp || []).map((item) => ({ ...item, campaign })));
    panel.hidden = !items.length;
    panel.replaceChildren(...items.map((item) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = `${item.label || 'Special add-on'} — ${item.display_price || ''}`.trim();
      button.addEventListener('click', async () => {
        try {
          this.message('Adding special add-on…', 'loading');
          await this.request('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(item.variant_id), quantity: 1, properties: { _wsp_campaign: item.campaign.id, _wsp_pwp: 'true' } }] }) });
          await this.sync();
        } catch (error) { this.message('Special add-on could not be added. Please try again.', 'error'); }
      });
      return button;
    }));
  }

  blockOverLimit(event) {
    const form = event.target;
    const id = form instanceof HTMLFormElement ? form.querySelector('[name="id"]')?.value : null;
    const add = Number(form?.querySelector?.('[name="quantity"]')?.value || 1);
    if (!id || !this.lastCart) return;
    for (const campaign of this.campaigns) {
      const maximum = Number(campaign.conditions?.maximum_quantity || 0);
      const variants = this.ids(campaign.eligibility?.variant_ids);
      if (maximum && variants.has(String(id)) && this.evaluate(this.lastCart, campaign).quantity + add > maximum) {
        event.preventDefault();
        this.message(campaign.limit_message || `This promotion is limited to ${maximum} eligible items.`, 'error');
      }
    }
  }
}

const bootPromotionEngine = () => {
  if (window.WSPPromotionEngine) return;
  const node = document.getElementById('wsp-promotion-config');
  if (!node) return;
  try {
    const engine = new PromotionEngine(JSON.parse(node.textContent || '{}'), document.getElementById('wsp-promotion-status'));
    window.WSPPromotionEngine = engine;
    if (!engine.campaigns.length) return;
    void engine.sync();
    document.addEventListener('cart:update', () => void engine.sync());
    document.addEventListener('submit', (event) => engine.blockOverLimit(event), true);
  } catch (error) { console.error('[WSP Promotion Engine]', error); }
};

const bootLocaleVoucher = () => {
  if (window.WSPLocaleVoucher) return;
  const node = document.getElementById('wsp-locale-voucher-config');
  if (!node) return;

  let config;
  try {
    config = JSON.parse(node.textContent || '{}');
  } catch (error) {
    console.error('[WSP Locale Voucher]', error);
    return;
  }

  const code = String(config.code || '').trim();
  const eligibleVariants = new Set((config.variant_ids || []).map(String));
  const allowedLocales = new Set((config.locales || []).map((locale) => String(locale).toLowerCase()));
  const root = window.Shopify?.routes?.root || '/';
  let running = false;
  let queued = false;
  let lastAttempt = '';

  const currentLocale = () => String(document.documentElement.lang || '').toLowerCase().split('-')[0];
  const isActive = () => {
    const now = Date.now();
    return now >= Date.parse(config.starts_at) && now < Date.parse(config.ends_at);
  };
  const appliedCodes = (cart) => {
    const values = [];
    for (const application of cart.cart_level_discount_applications || []) {
      if (application.type === 'discount_code' && application.title) values.push(application.title);
    }
    for (const item of cart.items || []) {
      for (const allocation of item.line_level_discount_allocations || item.discount_allocations || []) {
        const application = allocation.discount_application || {};
        if (application.type === 'discount_code' && application.title) values.push(application.title);
      }
    }
    return [...new Set(values)];
  };
  const refreshCart = async (cart) => {
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : '')
      .filter(Boolean);
    let sections = {};
    if (sectionIds.length) {
      const response = await fetch(`${root}?${new URLSearchParams({ sections: sectionIds.join(',') })}`, {
        headers: { Accept: 'application/json' },
      });
      if (response.ok) sections = await response.json();
    }
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'wsp-locale-voucher',
        data: { source: 'wsp-locale-voucher', itemCount: 0, sections },
      },
    }));
  };
  const sync = async () => {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    try {
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      const codes = appliedCodes(cart);
      const hasCode = codes.some((value) => value.toLowerCase() === code.toLowerCase());
      const hasEligibleItem = (cart.items || []).some((item) => eligibleVariants.has(String(item.variant_id)));
      const shouldApply = isActive() && allowedLocales.has(currentLocale()) && hasEligibleItem;
      if (shouldApply === hasCode) return;

      const nextCodes = codes.filter((value) => value.toLowerCase() !== code.toLowerCase());
      if (shouldApply) nextCodes.push(code);
      const attempt = `${currentLocale()}|${shouldApply}|${(cart.items || []).map((item) => `${item.variant_id}:${item.quantity}`).join(',')}|${nextCodes.join(',')}`;
      if (attempt === lastAttempt) return;
      lastAttempt = attempt;

      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: nextCodes.join(',') }),
      });
      if (!response.ok) return;
      await refreshCart(await response.json());
    } catch (error) {
      console.error('[WSP Locale Voucher]', error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        void sync();
      }
    }
  };

  window.WSPLocaleVoucher = { sync };
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.sourceId !== 'wsp-locale-voucher') void sync();
  });
  void sync();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    bootPromotionEngine();
    bootLocaleVoucher();
  }, { once: true });
} else {
  bootPromotionEngine();
  bootLocaleVoucher();
}

window.WSPRefreshAndOpenCartDrawer = async ({ sourceId = 'wsp-promotion', itemCount = 1, variantId = '' } = {}) => {
  if (window.WSPPromotionEngine) await window.WSPPromotionEngine.sync();

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((item) => item instanceof HTMLElement ? item.dataset.sectionId : '')
    .filter(Boolean);
  const root = window.Shopify?.routes?.root || '/';
  const sectionUrl = `${root}?${new URLSearchParams({ sections: sectionIds.join(',') })}`;
  const [cartResponse, sectionsResponse] = await Promise.all([
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }),
    sectionIds.length ? fetch(sectionUrl, { headers: { Accept: 'application/json' } }) : Promise.resolve(null),
  ]);
  if (!cartResponse.ok || (sectionsResponse && !sectionsResponse.ok)) throw new Error('Cart drawer refresh failed');

  const cart = await cartResponse.json();
  const paidItemCount = (cart.items || []).reduce(
    (total, item) => item.properties?._wsp_promotion_gift === 'true' ? total : total + item.quantity,
    0
  );
  const sections = sectionsResponse ? await sectionsResponse.json() : {};
  document.dispatchEvent(new CustomEvent('cart:update', {
    bubbles: true,
    detail: {
      resource: cart,
      sourceId,
      data: { source: sourceId, itemCount: paidItemCount, variantId: String(variantId), sections },
    },
  }));

  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') drawer.open();
};
